/**
 * settlement_plan.ts — turn a deterministic SettlementSite into an engine CityPlan.
 *
 * The engine `CityGenerator` is pure data (PCG32-seeded, no Three.js): given a
 * `CityConfig` + a height sampler it returns roads / blocks / parcels / building
 * pads / street props in LOCAL coordinates centered at (0,0). We seed it from the
 * site's `siteSeed` so the layout is deterministic, and feed it a height sampler
 * that maps local plan coords → world terrain so pads sit on the real ground.
 *
 * 2026-07-20 layout overhaul (research-driven — real cities, not toy rings):
 *  · Urban dimensions follow real morphology: blocks on the order of 60–120 m
 *    (Manhattan 80×274 m / Chicago 100×200 m are the canonical references),
 *    street hierarchy arterial → collector → local, and parcel areas sized so
 *    downtown lots hold towers while edge lots hold low-rise.
 *  · PLAN RADIUS IS ADAPTIVE: each tier wants a much larger radius than its
 *    reserved SETTLEMENT_FOOTPRINT (cities 150 → 420 m), clamped per-site so
 *    the plan never reaches a neighboring settlement's reserved footprint —
 *    site POSITIONS never move (the world must stay stable), only their
 *    geometry grows into the free space around them.
 *  · Density reads as a GRADIENT at render time (the settlements module bands
 *    building heights by radial distance — Gaussian-falloff skylines are the
 *    standard procedural-city device); the plan's job is to supply MANY
 *    well-formed pads (cities target hundreds, not 72).
 */
import { createCityConfig } from 'enginex/city/config.js';
import type { CityConfig, CityPlan } from 'enginex/city/config.js';
import { terrainHeight, plateauFor } from '../../../shared/terrain.js';
import { SETTLEMENT_FOOTPRINT, settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { archetypeForSite } from './settlement_archetypes.js';
import { finalizeSettlementPlan } from './districts.js';
import { civilizationOf as capCivOf, districtProgram, type Civilization, type DistrictZoning } from '../../../shared/capitals.js';

/** Desired plan radius per tier — the "huge city" dial. Actual radius adapts
 *  down per site so plans never collide with a neighbor's reserved footprint. */
export const PLAN_RADIUS_DESIRED: Record<SettlementSite['tier'], number> = {
  city: 420, town: 180, hamlet: 95,
  // Capital wave (2026-09-15): the capital's "plan" is its skeleton out to the
  // wall; districts are fixed city-scale plans; the campus plaza is town-scale.
  capital: 3230, district: 420, circuit: 180,
};
/** Clearance kept between a plan edge and a neighbor's reserved footprint. */
const PLAN_NEIGHBOR_GAP_M = 40;
/** Road falloff (8) + pad bevel (5) + slack kept between a plan edge and its plateau rim. */
const PLATEAU_PLAN_MARGIN = 20;

const sitesCache = new Map<string, SettlementSite[]>();
function sitesFor(seed: string): SettlementSite[] {
  let s = sitesCache.get(seed);
  if (!s) { s = settlementSites(seed); sitesCache.set(seed, s); }
  return s;
}

/**
 * Per-site adaptive plan radius: as large as the tier wants, shrunk when a
 * neighboring site's reserved footprint is close, never below the tier's own
 * footprint (the pre-overhaul behavior). Deterministic — pure over the sites.
 */
export function planRadiusFor(site: SettlementSite, seed: string): number {
  const desired = PLAN_RADIUS_DESIRED[site.tier];
  // A capital's skeleton reaches its wall by construction (the picker cleared the
  // land); districts are laid out so their plans never touch — fixed radii. The
  // circuit's reservation (its footprint, 500 m) is the stadium disc, not its
  // plaza plan (180 m) — the footprint floor below must not inflate it.
  if (site.tier === 'capital' || site.tier === 'circuit') return desired;
  const floor = SETTLEMENT_FOOTPRINT[site.tier];
  let r = desired;
  for (const other of sitesFor(seed)) {
    if (other.id === site.id) continue;
    // Same-capital members (the capital and its districts) never shrink each other.
    if (site.capitalId !== undefined && other.capitalId === site.capitalId) continue;
    const d = Math.hypot(other.centerX - site.centerX, other.centerZ - site.centerZ);
    if (d > desired + 200) continue; // far — cannot constrain
    const maxHere = d - SETTLEMENT_FOOTPRINT[other.tier] - PLAN_NEIGHBOR_GAP_M;
    if (maxHere < r) r = maxHere;
  }
  // Settlement plateaus (terrain wave 2026-09-08): the plan must stay INSIDE the
  // level disc (minus the road falloff + pad bevel margin) so every foundation
  // grades to one height — a plan spilling onto the blend ring would put its
  // rim on a ramp.
  const plateau = plateauFor(seed, site.id);
  if (plateau && plateau.r - PLATEAU_PLAN_MARGIN < r) r = plateau.r - PLATEAU_PLAN_MARGIN;
  return Math.max(floor, Math.round(r));
}

/** Build the per-tier CityConfig. `radius` comes from planRadiusFor (callers
 *  without site context fall back to the reserved footprint). Exported for verify. */
export function tierConfig(tier: SettlementSite['tier'], siteSeed: number, radius?: number, zoning?: DistrictZoning): CityConfig {
  const r = radius ?? SETTLEMENT_FOOTPRINT[tier];
  const center = { x: 0, z: 0 };
  const archetype = archetypeForSite(tier, siteSeed); // engine city-archetype (drives road/block/building layout)

  // Capital wave (2026-09-15): a district IS a city-scale plan; the campus plaza
  // is a town-scale plan; a capital's config exists only so callers can type it
  // (settlement_plan_gen short-circuits it to an empty plan).
  if (tier === 'city' || tier === 'district' || tier === 'capital') {
    // A capital district's zoning follows its PROGRAM (phase 2); a classic city
    // keeps the mixed downtown weights.
    const ZW: Record<DistrictZoning, { residential: number; commercial: number; industrial: number; park: number }> = {
      wards: { residential: 0.7, commercial: 0.15, industrial: 0.05, park: 0.1 }, commerce: { residential: 0.25, commercial: 0.55, industrial: 0.08, park: 0.12 },
      industry: { residential: 0.2, commercial: 0.2, industrial: 0.5, park: 0.1 }, gardens: { residential: 0.35, commercial: 0.15, industrial: 0.1, park: 0.4 },
      civic: { residential: 0.3, commercial: 0.5, industrial: 0.05, park: 0.15 }, mixed: { residential: 0.4, commercial: 0.35, industrial: 0.1, park: 0.15 },
    };
    const zoningWeights = zoning ? ZW[zoning] : { residential: 0.34, commercial: 0.40, industrial: 0.14, park: 0.12 };
    // A real urban core: dense-grid archetype (the one layout that reliably
    // closes blocks at metro radii — the radial archetypes close only a few
    // faces and leave the wheel empty), blocks ~60–120 m, hundreds of pads.
    return createCityConfig('city', {
      seed: siteSeed, radius: r, center, archetype,
      roads: { style: 'mixed', arterialSpacing: 220, collectorSpacing: 110, localSpacing: 46, maxGrade: 0.08, segmentLength: 16, snapRadius: 10, minIntersectionAngle: 28, arterialHalfWidth: 8, collectorHalfWidth: 5, localHalfWidth: 3.5, sidewalkWidth: 3, curbHeight: 0.15, crownHeight: 0.04 },
      blocks: { minArea: 1600, maxAspectRatio: 5 },
      // Megastructure lots: a 33 m-wide supertall needs a ~35–45 m pad →
      // parcels sized 300–1400 m² (downtown tower lots, not house lots).
      parcels: { subdivisionStyle: 'mixed', targetArea: { min: 240, max: 1400 } },
      buildings: { maxFloors: 60, minSetback: 2, maxCoverage: 0.78, minParcelArea: 170, zoningWeights },
      performance: { maxBuildings: 520, instanceBatching: true, lodDistances: [520, 1100] },
    });
  }

  if (tier === 'town' || tier === 'circuit') {
    return createCityConfig('town', {
      seed: siteSeed, radius: r, center, archetype,
      roads: { style: 'grid', arterialSpacing: 130, collectorSpacing: 58, localSpacing: 26, maxGrade: 0.10, segmentLength: 15, snapRadius: 10, minIntersectionAngle: 28, arterialHalfWidth: 5, collectorHalfWidth: 4, localHalfWidth: 3, sidewalkWidth: 2, curbHeight: 0.15, crownHeight: 0.05 },
      blocks: { minArea: 520, maxAspectRatio: 6 },
      parcels: { subdivisionStyle: 'obb', targetArea: { min: 90, max: 380 } },
      buildings: { maxFloors: 5, minSetback: 2, maxCoverage: 0.72, minParcelArea: 60, zoningWeights: { residential: 0.52, commercial: 0.32, industrial: 0.07, park: 0.09 } },
      performance: { maxBuildings: 150, instanceBatching: true, lodDistances: [240, 520] },
    });
  }

  // hamlet — tight lanes; small lots so a ~95 m cluster still reads as a village.
  return createCityConfig('town', {
    seed: siteSeed, radius: r, center, archetype,
    roads: { style: 'grid', arterialSpacing: 78, collectorSpacing: 46, localSpacing: 18, maxGrade: 0.14, segmentLength: 12, snapRadius: 7, minIntersectionAngle: 25, arterialHalfWidth: 4, collectorHalfWidth: 3, localHalfWidth: 2.5, sidewalkWidth: 1.5, curbHeight: 0.15, crownHeight: 0.05 },
    blocks: { minArea: 110, maxAspectRatio: 6 },
    parcels: { subdivisionStyle: 'obb', targetArea: { min: 50, max: 230 } },
    buildings: { maxFloors: 2, minSetback: 1.5, maxCoverage: 0.64, minParcelArea: 40, zoningWeights: { residential: 0.74, commercial: 0.14, industrial: 0.04, park: 0.08 } },
    performance: { maxBuildings: 32, instanceBatching: true, lodDistances: [110, 260] },
  });
}

/** Radial skyline band: target building height (m) by distance fraction from
 *  the settlement core — the Gaussian-falloff skyline (tall downtown, mid
 *  ring, low rim) that real cities and the procedural-city literature share.
 *  Pure; the settlements renderer applies it to the real-meter civ models.
 *  MONUMENTAL SCALE (user-directed ×10 wave, 2026-07-22): the pad-fit crush
 *  that held towns at 4–60 m is gone (the renderer caps footprints by the
 *  street lattice instead), so these bands are what actually renders. Towns
 *  jump ~×5–8 (225–420 m cores — the "wow" tier the user asked ×10 for),
 *  cities push past today's supertalls (320–460 m cores), hamlets ×3. */
export function civTargetHeight(tier: SettlementSite['tier'], distFrac: number, r01: number): number {
  // district: the city bands until the capital height envelope lands (phase 2)
  if (tier === 'city' || tier === 'district' || tier === 'capital') {
    if (distFrac < 0.3) return 320 + r01 * 140; // supertall core (320–460 m)
    if (distFrac < 0.62) return 150 + r01 * 110; // high-rise ring (150–260 m)
    return 75 + r01 * 60;                        // mid-rise rim (75–135 m)
  }
  if (tier === 'town' || tier === 'circuit') return distFrac < 0.4 ? 225 + r01 * 195 : 135 + r01 * 120;
  return 48 + r01 * 36; // hamlet (48–84 m)
}

export interface HeightBand { lo: number; hi: number }
/** Capital district height bands by program zoning (spec §7). */
export const DISTRICT_BANDS: Record<DistrictZoning, HeightBand> = {
  civic: { lo: 120, hi: 350 }, commerce: { lo: 120, hi: 350 }, wards: { lo: 12, hi: 100 },
  industry: { lo: 15, hi: 60 }, gardens: { lo: 12, hi: 40 }, mixed: { lo: 30, hi: 140 },
};
/** The capital core's skyline anchors (phase 3 places them). */
export const CAPITAL_CORE_BAND: Record<Civilization, HeightBand> = { a: { lo: 450, hi: 650 }, b: { lo: 500, hi: 700 } };
/** Where the envelope's falloff starts (ring 1) and ends (the wall) — capital-centre distances. */
const ENVELOPE_FULL_M = 950, ENVELOPE_SPAN_M = 1800, ENVELOPE_FALLOFF = 0.45;
/** A commerce/civic district's own core reads taller: +30 % at its centre, Gaussian over 250 m. */
const CLUSTER_BOOST = 0.3, CLUSTER_SIGMA_M = 250;

/**
 * ONE height dispatcher for both readers (the settlements module and the proxy
 * bake). Classic tiers keep `civTargetHeight`; a district takes its program's
 * band × a falloff from the CAPITAL centre (ring 1 full, the wall 55 %) × a
 * cluster boost at a commerce/civic district's own centre — a capital-level
 * envelope, not thirty mounds. Pure over (site, seed, pad centre, draw).
 */
export function targetHeightFor(site: SettlementSite, seed: string, cx: number, cz: number, distFrac: number, r01: number): number {
  if (site.tier === 'capital') { const b = CAPITAL_CORE_BAND[capCivOf(site.siteSeed)]; return b.lo + (b.hi - b.lo) * r01; }
  if (site.tier !== 'district' || site.capitalId === undefined) return civTargetHeight(site.tier, distFrac, r01);
  const cap = sitesFor(seed).find((s) => s.id === site.capitalId);
  if (!cap) return civTargetHeight('city', distFrac, r01);
  const prog = districtProgram(capCivOf(cap.siteSeed), site.district ?? 0);
  const band = DISTRICT_BANDS[prog.zoning];
  const dCap = Math.hypot(site.centerX + cx - cap.centerX, site.centerZ + cz - cap.centerZ);
  const falloff = 1 - ENVELOPE_FALLOFF * Math.max(0, Math.min(1, (dCap - ENVELOPE_FULL_M) / ENVELOPE_SPAN_M));
  const dOwn = Math.hypot(cx, cz);
  const cluster = prog.zoning === 'commerce' || prog.zoning === 'civic' ? 1 + CLUSTER_BOOST * Math.exp(-(dOwn * dOwn) / (2 * CLUSTER_SIGMA_M * CLUSTER_SIGMA_M)) : 1;
  const h = (band.lo + (band.hi - band.lo) * r01) * falloff * cluster;
  return Math.min(band.hi * 1.35, Math.max(band.lo * 0.8, h));
}

/** Height-aware clear gap between neighbors — the ENCLOSURE dial. At the
 *  monumental scale (×10 wave) footprints already span whole blocks (the
 *  lattice cap), so the gap only keeps facades from fusing: 0.1·h capped at
 *  8 m — near-touching Kowloon-density street walls; the infill retry turns
 *  any rejection into a mid-rise, so the frontage never opens into voids. */
export function civGapFor(scaledH: number): number {
  return Math.min(8, Math.max(2.5, scaledH * 0.1));
}

const planCache = new Map<string, CityPlan>();

/** Internal cache write for the generator half (lib/settlement_plan_gen).
 *  EVERY plan passes finalizeSettlementPlan exactly once at a cache-write
 *  choke point (here or primeSettlementPlan) — main thread, both workers,
 *  bakers and verify therefore all see the same finalized plan. */
export function _cachePlan(seed: string, siteSeed: number, plan: CityPlan): void {
  finalizeSettlementPlan(plan, seed, siteSeed);
  planCache.set(`${seed}:${siteSeed}`, plan);
}

/**
 * Inject a WORKER-generated plan into THIS thread's memo (perf wave 3 — a
 * city-tier plan is an expensive CityGenerator run; generating it on the main
 * thread was the biggest traversal hitch in the perf harness). The worker
 * strips `fields` (closures, not structured-cloneable); no main-side
 * consumer reads plan.fields.
 */
/** Stable fnv1a-32 of the seed — baked-plan filenames carry it so a terrain-seed
 *  change invalidates every static plan (client fetch 404s → worker fallback). */
export function planFileFor(seed: string, siteId: number): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return `${(h >>> 0).toString(16)}-${siteId}.json`;
}

export function primeSettlementPlan(seed: string, siteSeed: number, plan: CityPlan): void {
  const key = `${seed}:${siteSeed}`;
  if (!planCache.has(key)) {
    finalizeSettlementPlan(plan, seed, siteSeed); // idempotent (baked plans arrive pre-final)
    planCache.set(key, plan);
  }
}

/** Memo-only read: the plan if THIS thread already holds it, else null. Never
 *  generates — sync consumers on the hot path (terrain stamping) use this so a
 *  cache miss can't turn into a multi-second main-thread CityGenerator run. */
export function peekSettlementPlan(seed: string, siteSeed: number): CityPlan | null {
  return planCache.get(`${seed}:${siteSeed}`) ?? null;
}

